import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "resource-list" }
const _hoisted_2 = { class: "list-header" }
const _hoisted_3 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_4 = {
  key: 1,
  class: "empty-container"
}
const _hoisted_5 = {
  key: 2,
  class: "resource-cards"
}
const _hoisted_6 = { class: "card-header" }
const _hoisted_7 = { class: "title-section" }
const _hoisted_8 = { class: "actions" }
const _hoisted_9 = { class: "card-content" }
const _hoisted_10 = {
  key: 0,
  class: "content-preview"
}
const _hoisted_11 = {
  key: 1,
  class: "resource-url"
}
const _hoisted_12 = {
  key: 2,
  class: "referral-info"
}
const _hoisted_13 = {
  key: 0,
  class: "info-item"
}
const _hoisted_14 = {
  key: 1,
  class: "info-item"
}
const _hoisted_15 = {
  key: 2,
  class: "info-item"
}
const _hoisted_16 = { class: "meta-info" }
const _hoisted_17 = { class: "time" }
const _hoisted_18 = {
  key: 0,
  class: "tags"
}
const _hoisted_19 = {
  key: 3,
  class: "pagination-container"
}

import { ref, computed, onMounted, watch } from 'vue';
import { ElMessage, ElMessageBox, FormInstance } from 'element-plus';
import { Plus, Link, Edit, Delete } from '@element-plus/icons-vue';
import type { Resource, AddResourceRequest, UpdateResourceRequest } from '@/types/resource';
import { ResourceType } from '@/types/resource';
import resourceApi from '@/services/resourceApi';
import { formatDate } from '@/utils/format';
import TagSelect from '@/components/tag/TagSelect.vue';
import { usePositionStore } from '@/store/positionStore';


export default /*@__PURE__*/_defineComponent({
  __name: 'ResourceList',
  props: {
    companyId: {},
    positionId: {}
  },
  emits: ["refresh"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// 状态变量
const loading = ref<boolean>(false);
const resources = ref<Resource[]>([]);
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);
const total = ref<number>(0);
const dialogVisible = ref<boolean>(false);
const editingResource = ref<Resource | null>(null);
const formRef = ref<FormInstance>();

// 表单数据
const form = ref<AddResourceRequest>({
  companyId: props.companyId!,
  positionId: props.positionId,
  type: ResourceType.INTERVIEW_EXPERIENCE,
  title: '',
  url: '',
  content: '',
  visibility: 'private',
  referralContact: '',
  referralCode: '',
  referralDeadline: '',
  tagIds: []
});

// 表单校验规则
const rules = {
  type: [{ required: true, message: '请选择资源类型' }],
  title: [{ required: true, message: '请输入资源标题' }],
  visibility: [{ required: true, message: '请选择可见性' }]
};

// 资源类型选项
const resourceTypes = [
  { label: '面试经验', value: ResourceType.INTERVIEW_EXPERIENCE },
  { label: '内推信息', value: ResourceType.REFERRAL },
  { label: '公司信息', value: ResourceType.COMPANY_INFO },
  { label: '其他', value: ResourceType.OTHER }
];

// 获取资源类型标签
const getResourceTypeTag = (type: ResourceType) => {
  switch (type) {
    case ResourceType.INTERVIEW_EXPERIENCE:
      return { type: 'primary', label: '面试经验' };
    case ResourceType.REFERRAL:
      return { type: 'success', label: '内推信息' };
    case ResourceType.COMPANY_INFO:
      return { type: 'warning', label: '公司信息' };
    case ResourceType.OTHER:
      return { type: 'info', label: '其他' };
    default:
      return { type: 'info', label: '未知' };
  }
};

// 监听props变化
watch(() => props.companyId, (newVal) => {
  if (newVal) {
    currentPage.value = 1;
    loadResources();
  }
});

watch(() => props.positionId, (newVal) => {
  if (newVal) {
    currentPage.value = 1;
    loadResources();
  }
});

// 加载资源列表
const loadResources = async () => {
  if (!props.companyId && !props.positionId) {
    return;
  }

  loading.value = true;
  try {
    let response;
    if (props.positionId) {
      console.log('Loading resources for position:', props.positionId);
      response = await resourceApi.getResourcesByPosition(
        props.positionId,
        currentPage.value,
        pageSize.value
      );
    } else if (props.companyId) {
      console.log('Loading resources for company:', props.companyId);
      response = await resourceApi.getResourcesByCompany(
        props.companyId,
        currentPage.value,
        pageSize.value
      );
    }
    console.log('Received response:', response);
    if (response) {
      console.log('Setting resources:', response.content);
      console.log('Setting total:', response.totalElements);
      resources.value = response.content || [];
      total.value = response.totalElements || 0;
    }
  } catch (error) {
    console.error('加载资源列表失败:', error);
    ElMessage.error('加载资源列表失败');
    resources.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};

// 处理添加资源
const handleAddResource = () => {
  editingResource.value = null;
  if (!props.companyId && !props.positionId) {
    ElMessage.warning('请先选择一个公司或职位');
    return;
  }
  
  form.value = {
    companyId: props.companyId || 0,
    positionId: props.positionId,
    type: ResourceType.INTERVIEW_EXPERIENCE,
    title: '',
    url: '',
    content: '',
    visibility: 'private',
    referralContact: '',
    referralCode: '',
    referralDeadline: '',
    tagIds: []
  };
  dialogVisible.value = true;
};

// 处理编辑资源
const handleEdit = (resource: Resource) => {
  editingResource.value = resource;
  form.value = {
    ...resource,
    tagIds: resource.tags?.map(tag => tag.id) || []
  };
  dialogVisible.value = true;
};

// 处理删除资源
const handleDelete = async (resource: Resource) => {
  try {
    await ElMessageBox.confirm('确定要删除该资源吗？', '提示', {
      type: 'warning'
    });
    await resourceApi.deleteResource(resource.id!);
    ElMessage.success('删除成功');
    loadResources();
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除资源失败:', error);
      ElMessage.error('删除资源失败');
    }
  }
};

// 处理表单提交
const handleSubmit = async () => {
  if (!formRef.value) return;
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        // 如果没有选择公司，但有选择职位，则先获取职位对应的公司ID
        if (!form.value.companyId && form.value.positionId) {
          const positionStore = usePositionStore();
          
          // 加载职位详情
          try {
            await positionStore.getPosition(form.value.positionId);
            if (positionStore.currentPosition?.company?.id) {
              form.value.companyId = positionStore.currentPosition.company.id;
            } else {
              ElMessage.error('无法确定资源关联的公司，请重试');
              return;
            }
          } catch (error) {
            console.error('获取职位信息失败:', error);
            ElMessage.error('获取职位信息失败，无法确定关联公司');
            return;
          }
        }
        
        if (editingResource.value) {
          await resourceApi.updateResource(editingResource.value.id!, {
            id: editingResource.value.id!,
            companyId: form.value.companyId,
            title: form.value.title,
            content: form.value.content,
            visibility: form.value.visibility,
            referralContact: form.value.referralContact,
            referralCode: form.value.referralCode,
            referralDeadline: form.value.referralDeadline,
            tagIds: form.value.tagIds
          });
          ElMessage.success('更新成功');
        } else {
          await resourceApi.addResource(form.value);
          ElMessage.success('添加成功');
        }
        dialogVisible.value = false;
        loadResources();
        emit('refresh');
      } catch (error) {
        console.error('保存资源失败:', error);
        ElMessage.error('保存失败');
      }
    }
  });
};

// 处理分页
const handleSizeChange = (size: number) => {
  pageSize.value = size;
  loadResources();
};

const handleCurrentChange = (page: number) => {
  currentPage.value = page;
  loadResources();
};

// 初始化
onMounted(() => {
  loadResources();
});

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_skeleton = _resolveComponent("el-skeleton")!
  const _component_el_empty = _resolveComponent("el-empty")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_link = _resolveComponent("el-link")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_radio = _resolveComponent("el-radio")!
  const _component_el_radio_group = _resolveComponent("el-radio-group")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[14] || (_cache[14] = _createElementVNode("h3", null, "相关资源", -1)),
      _createVNode(_component_el_button, {
        type: "primary",
        size: "small",
        round: "",
        onClick: handleAddResource
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_icon, null, {
            default: _withCtx(() => [
              _createVNode(_unref(Plus))
            ]),
            _: 1
          }),
          _cache[13] || (_cache[13] = _createTextVNode(" 添加资源 "))
        ]),
        _: 1
      })
    ]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_component_el_skeleton, {
            rows: 3,
            animated: ""
          })
        ]))
      : (!resources.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(_component_el_empty, { description: "暂无相关资源" })
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resources.value, (resource) => {
              return (_openBlock(), _createBlock(_component_el_card, {
                key: resource.id,
                class: "resource-card",
                "body-style": { padding: '16px' }
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("h4", null, _toDisplayString(resource.title), 1),
                      _createVNode(_component_el_tag, {
                        type: getResourceTypeTag(resource.type).type,
                        size: "small",
                        effect: "light",
                        round: ""
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(getResourceTypeTag(resource.type).label), 1)
                        ]),
                        _: 2
                      }, 1032, ["type"]),
                      _createVNode(_component_el_tag, {
                        type: resource.visibility === 'public' ? 'success' : 'warning',
                        size: "small",
                        effect: "light",
                        round: ""
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(resource.visibility === 'public' ? '公开' : '私密'), 1)
                        ]),
                        _: 2
                      }, 1032, ["type"])
                    ]),
                    _createElementVNode("div", _hoisted_8, [
                      _createVNode(_component_el_button, {
                        type: "primary",
                        size: "small",
                        round: "",
                        text: "",
                        class: "action-button edit-button",
                        onClick: ($event: any) => (handleEdit(resource))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_icon, null, {
                            default: _withCtx(() => [
                              _createVNode(_unref(Edit))
                            ]),
                            _: 1
                          }),
                          _cache[15] || (_cache[15] = _createTextVNode(" 编辑 "))
                        ]),
                        _: 2
                      }, 1032, ["onClick"]),
                      _createVNode(_component_el_button, {
                        type: "danger",
                        size: "small",
                        round: "",
                        text: "",
                        class: "action-button delete-button",
                        onClick: ($event: any) => (handleDelete(resource))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_icon, null, {
                            default: _withCtx(() => [
                              _createVNode(_unref(Delete))
                            ]),
                            _: 1
                          }),
                          _cache[16] || (_cache[16] = _createTextVNode(" 删除 "))
                        ]),
                        _: 2
                      }, 1032, ["onClick"])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_9, [
                    (resource.content)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(resource.content.slice(0, 100)) + _toDisplayString(resource.content.length > 100 ? '...' : ''), 1))
                      : _createCommentVNode("", true),
                    (resource.url)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                          _createVNode(_component_el_link, {
                            href: resource.url,
                            target: "_blank",
                            type: "primary"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_el_icon, null, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(Link))
                                ]),
                                _: 1
                              }),
                              _cache[17] || (_cache[17] = _createTextVNode(" 查看原文 "))
                            ]),
                            _: 2
                          }, 1032, ["href"])
                        ]))
                      : _createCommentVNode("", true),
                    (resource.type === 'referral')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                          (resource.referralContact)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                _cache[18] || (_cache[18] = _createElementVNode("span", { class: "label" }, "推荐人：", -1)),
                                _createElementVNode("span", null, _toDisplayString(resource.referralContact), 1)
                              ]))
                            : _createCommentVNode("", true),
                          (resource.referralCode)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                _cache[19] || (_cache[19] = _createElementVNode("span", { class: "label" }, "推荐码：", -1)),
                                _createElementVNode("span", null, _toDisplayString(resource.referralCode), 1)
                              ]))
                            : _createCommentVNode("", true),
                          (resource.referralDeadline)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                _cache[20] || (_cache[20] = _createElementVNode("span", { class: "label" }, "截止日期：", -1)),
                                _createElementVNode("span", null, _toDisplayString(_unref(formatDate)(resource.referralDeadline)), 1)
                              ]))
                            : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_16, [
                      _createElementVNode("span", _hoisted_17, _toDisplayString(_unref(formatDate)(resource.createTime)), 1),
                      (resource.tags?.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resource.tags, (tag) => {
                              return (_openBlock(), _createBlock(_component_el_tag, {
                                key: tag.id,
                                size: "small",
                                effect: "plain",
                                round: ""
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(tag.name), 1)
                                ]),
                                _: 2
                              }, 1024))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ])
                ]),
                _: 2
              }, 1024))
            }), 128))
          ])),
    (total.value > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
          _createVNode(_component_el_pagination, {
            "current-page": currentPage.value,
            "onUpdate:currentPage": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
            "page-size": pageSize.value,
            "onUpdate:pageSize": _cache[1] || (_cache[1] = ($event: any) => ((pageSize).value = $event)),
            "page-sizes": [10, 20, 50],
            total: total.value,
            layout: "total, sizes, prev, pager, next",
            onSizeChange: handleSizeChange,
            onCurrentChange: handleCurrentChange
          }, null, 8, ["current-page", "page-size", "total"])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_el_dialog, {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((dialogVisible).value = $event)),
      title: editingResource.value ? '编辑资源' : '添加资源',
      width: "600px"
    }, {
      footer: _withCtx(() => [
        _createVNode(_component_el_button, {
          onClick: _cache[11] || (_cache[11] = ($event: any) => (dialogVisible.value = false))
        }, {
          default: _withCtx(() => _cache[23] || (_cache[23] = [
            _createTextVNode("取消")
          ])),
          _: 1
        }),
        _createVNode(_component_el_button, {
          type: "primary",
          onClick: handleSubmit
        }, {
          default: _withCtx(() => _cache[24] || (_cache[24] = [
            _createTextVNode("确定")
          ])),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_form, {
          ref_key: "formRef",
          ref: formRef,
          model: form.value,
          rules: rules,
          "label-width": "100px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: "资源类型",
              prop: "type"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_select, {
                  modelValue: form.value.type,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.type) = $event)),
                  placeholder: "请选择资源类型"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(resourceTypes, (type) => {
                      return _createVNode(_component_el_option, {
                        key: type.value,
                        label: type.label,
                        value: type.value
                      }, null, 8, ["label", "value"])
                    }), 64))
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "标题",
              prop: "title"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.value.title,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.title) = $event)),
                  placeholder: "请输入资源标题"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "URL",
              prop: "url"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.value.url,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.value.url) = $event)),
                  placeholder: "请输入资源链接"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "内容",
              prop: "content"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.value.content,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.value.content) = $event)),
                  type: "textarea",
                  rows: 4,
                  placeholder: "请输入资源内容"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "可见性",
              prop: "visibility"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_radio_group, {
                  modelValue: form.value.visibility,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.value.visibility) = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_radio, { label: "public" }, {
                      default: _withCtx(() => _cache[21] || (_cache[21] = [
                        _createTextVNode("公开")
                      ])),
                      _: 1
                    }),
                    _createVNode(_component_el_radio, { label: "private" }, {
                      default: _withCtx(() => _cache[22] || (_cache[22] = [
                        _createTextVNode("私密")
                      ])),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            (form.value.type === 'referral')
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(_component_el_form_item, {
                    label: "推荐人",
                    prop: "referralContact"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_input, {
                        modelValue: form.value.referralContact,
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.value.referralContact) = $event)),
                        placeholder: "请输入推荐人"
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_form_item, {
                    label: "推荐码",
                    prop: "referralCode"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_input, {
                        modelValue: form.value.referralCode,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((form.value.referralCode) = $event)),
                        placeholder: "请输入推荐码"
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_form_item, {
                    label: "截止日期",
                    prop: "referralDeadline"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_date_picker, {
                        modelValue: form.value.referralDeadline,
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((form.value.referralDeadline) = $event)),
                        type: "date",
                        placeholder: "请选择截止日期"
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  })
                ], 64))
              : _createCommentVNode("", true),
            _createVNode(_component_el_form_item, {
              label: "标签",
              prop: "tagIds"
            }, {
              default: _withCtx(() => [
                _createVNode(TagSelect, {
                  modelValue: form.value.tagIds,
                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.value.tagIds) = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})